import { buildCollection } from "@firecms/core";
import { Button } from "@firecms/ui";

export function PhotoUrlField({
    property,
    value,
    setValue
}: any) {
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px", width: "100%", marginTop: "16px", marginBottom: "16px" }}>
            <span style={{ fontSize: "14px", fontWeight: "500", color: "#666" }}>{property.name}</span>
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                <img 
                    src={value || "/images/default_avatar.svg"} 
                    alt="Avatar Preview" 
                    onError={(e) => {
                        e.currentTarget.src = "/images/default_avatar.svg";
                    }}
                    style={{ width: "64px", height: "64px", borderRadius: "50%", objectFit: "cover", border: "1px solid #ccc", backgroundColor: "#f0f0f0" }} 
                />
                <input
                    type="text"
                    value={value || ""}
                    onChange={(e) => setValue(e.target.value || null)}
                    placeholder="Paste photo URL here"
                    style={{
                        flexGrow: 1,
                        padding: "10px 14px",
                        borderRadius: "6px",
                        border: "1px solid #ccc",
                        fontSize: "14px",
                        backgroundColor: "#fff",
                        color: "#333"
                    }}
                />
            </div>
        </div>
    );
}

export const profileCollection = buildCollection({
    id: "resume",
    name: "Resume",
    icon: "people",
    description: "Manage user profiles, professional bios, and scoring metadata.",
    path: "resume",
    defaultSize: "m",
    customId: false,
    properties: {
        name: {
            dataType: "string",
            name: "Name",
            hideFromCollection: true
        },
        first_name: {
            dataType: "string",
            name: "First Name",
            hideFromCollection: true,
            validation: { required: true }
        },
        last_nane: {
            dataType: "string",
            name: "Last Name (Typo in DB)",
            hideFromCollection: true,
            validation: { required: true }
        },
        bio: {
            dataType: "string",
            name: "Bio",
            multiline: true
        },
        photo_url: {
            dataType: "string",
            name: "Photo URL",
            Field: PhotoUrlField,
            hideFromCollection: true
        },
        is_open_to_work: {
            dataType: "boolean",
            name: "Open to Work"
        },
        location: {
            dataType: "string",
            name: "Location",
            hideFromCollection: true,
            validation: { required: true }
        },
        organization: {
            dataType: "string",
            name: "Organization"
        },
        user_function: {
            dataType: "array",
            name: "User Function",
            hideFromCollection: true,
            of: {
                dataType: "map",
                properties: {
                    key: { dataType: "string", name: "Key" },
                    label: { dataType: "string", name: "Label" },
                    value: { dataType: "number", name: "Value" }
                }
            }
        },
        title: {
            dataType: "string",
            name: "Title",
            validation: { required: true }
        },
        // Automatically populated fields (read-only)
        clean_url: {
            dataType: "string",
            name: "Clean URL",
            readOnly: true,
            hideFromCollection: true
        },
        commitment_type: {
            dataType: "array",
            name: "Commitment Type",
            readOnly: true,
            hideFromCollection: true,
            of: {
                dataType: "string"
            }
        },
        commitment_type_breakdown: {
            dataType: "array",
            name: "Commitment Type Breakdown",
            readOnly: true,
            hideFromCollection: true,
            of: {
                dataType: "map",
                properties: {
                    commitment_type: { dataType: "string", name: "Commitment Type" },
                    percentage: { dataType: "number", name: "Percentage" }
                }
            }
        },
        created_at: {
            dataType: "date",
            name: "Created At",
            autoValue: "on_create",
            readOnly: true
        },
        function: {
            dataType: "array",
            name: "Function",
            readOnly: true,
            of: {
                dataType: "string"
            }
        },
        function_breakdown: {
            dataType: "array",
            name: "Function Breakdown",
            readOnly: true,
            hideFromCollection: true,
            of: {
                dataType: "map",
                properties: {
                    function: { dataType: "string", name: "Function" },
                    percentage: { dataType: "number", name: "Percentage" }
                }
            }
        },
        function_focus: {
            dataType: "array",
            name: "Function Focus",
            readOnly: true,
            hideFromCollection: true,
            of: {
                dataType: "string"
            }
        },
        industry: {
            dataType: "array",
            name: "Industry",
            readOnly: true,
            of: {
                dataType: "string"
            }
        },
        industry_focus: {
            dataType: "array",
            name: "Industry Focus",
            readOnly: true,
            hideFromCollection: true,
            of: {
                dataType: "map",
                properties: {
                    industry: { dataType: "string", name: "Industry" },
                    percentage: { dataType: "number", name: "Percentage" }
                }
            }
        },
        is_verified: {
            dataType: "boolean",
            name: "Is Verified",
            readOnly: true,
            hideFromCollection: true
        },
        last_aggregated_at: {
            dataType: "date",
            name: "Last Aggregated At",
            readOnly: true
        },
        location_latlng: {
            dataType: "geopoint",
            name: "Location Geopoint",
            readOnly: true,
            hideFromCollection: true
        },
        references_count: {
            dataType: "number",
            name: "References Count",
            readOnly: true
        },
        score: {
            dataType: "map",
            name: "Score",
            readOnly: true,
            keyValue: true,
            hideFromCollection: true
        },
        score_tiers: {
            dataType: "array",
            name: "Score Tiers",
            readOnly: true,
            hideFromCollection: true,
            of: {
                dataType: "string"
            }
        },
        search_accomplishment_count: {
            dataType: "number",
            name: "Search Accomplishment Count",
            readOnly: true,
            hideFromCollection: true
        },
        search_companies: {
            dataType: "array",
            name: "Search Companies",
            readOnly: true,
            hideFromCollection: true,
            of: {
                dataType: "string"
            }
        },
        search_keywords: {
            dataType: "string",
            name: "Search Keywords",
            readOnly: true,
            hideFromCollection: true
        },
        search_total_refs: {
            dataType: "number",
            name: "Search Total References",
            readOnly: true,
            hideFromCollection: true
        },
        search_total_score: {
            dataType: "number",
            name: "Search Total Score",
            readOnly: true,
            hideFromCollection: true
        },
        search_total_years: {
            dataType: "number",
            name: "Search Total Years",
            readOnly: true,
            hideFromCollection: true
        },
        search_video_ref_count: {
            dataType: "number",
            name: "Search Video Reference Count",
            readOnly: true,
            hideFromCollection: true
        },
        search_text_ref_count: {
            dataType: "number",
            name: "Search Text Reference Count",
            readOnly: true,
            hideFromCollection: true
        },
        skills: {
            dataType: "array",
            name: "Skills",
            readOnly: true,
            hideFromCollection: true,
            of: {
                dataType: "string"
            }
        },
        skills_map: {
            dataType: "map",
            name: "Skills Map",
            readOnly: true,
            keyValue: true
        },
        updated_at: {
            dataType: "date",
            name: "Updated At",
            readOnly: true,
            hideFromCollection: true,
            autoValue: "on_update"
        },
        verified_accomplishments_count: {
            dataType: "number",
            name: "Verified Accomplishments Count",
            readOnly: true
        },
        // Flattened scoring fields (read-only)
        score_industry_saas: {
            dataType: "number",
            name: "Score Industry: SaaS",
            readOnly: true,
            hideFromCollection: true
        },
        score_industry_edtech: {
            dataType: "number",
            name: "Score Industry: EdTech",
            readOnly: true,
            hideFromCollection: true
        },
        score_function_engineering_development: {
            dataType: "number",
            name: "Score Function: Engineering/Development",
            readOnly: true,
            hideFromCollection: true
        },
        score_focus_platform_infrastructure_engineering: {
            dataType: "number",
            name: "Score Focus: Platform/Infrastructure",
            readOnly: true,
            hideFromCollection: true
        },
        score_focus_full_stack_development: {
            dataType: "number",
            name: "Score Focus: Full Stack Development",
            readOnly: true,
            hideFromCollection: true
        },
        score_commitment_fractional_contributor: {
            dataType: "number",
            name: "Score Commitment: Fractional Contributor",
            readOnly: true,
            hideFromCollection: true
        },
        score_commitment_type_fractional_contributor: {
            dataType: "number",
            name: "Score Commitment Type: Fractional Contributor",
            readOnly: true,
            hideFromCollection: true
        },
        score_function_focus_full_stack_development: {
            dataType: "number",
            name: "Score Function Focus: Full Stack Development",
            readOnly: true,
            hideFromCollection: true
        },
        score_function_focus_platform_infrastructure_engineering: {
            dataType: "number",
            name: "Score Function Focus: Platform/Infrastructure",
            readOnly: true,
            hideFromCollection: true
        }
    },
    openEntityMode: "full_screen",
    subcollections: [
        buildCollection({
            id: "accomplishment",
            name: "Accomplishment",
            path: "accomplishment",
            defaultSize: "xs",
            customId: false,
            properties: {
                title: {
                    dataType: "string",
                    name: "Title",
                    validation: { required: true }
                },
                role: {
                    dataType: "string",
                    name: "Role"
                },
                association: {
                    dataType: "string",
                    name: "Association"
                },
                summary: {
                    dataType: "string",
                    name: "Summary",
                    multiline: true
                },
                industry: {
                    dataType: "string",
                    name: "Industry"
                },
                function: {
                    dataType: "string",
                    name: "Function"
                },
                function_focus: {
                    dataType: "string",
                    name: "Function focus"
                },
                impact: {
                    dataType: "array",
                    name: "Impact",
                    hideFromCollection: true,
                    of: {
                        dataType: "string"
                    }
                },
                skills: {
                    dataType: "array",
                    name: "Skills",
                    hideFromCollection: true,
                    of: {
                        dataType: "string"
                    }
                },
                commitment_type: {
                    dataType: "string",
                    name: "Commitment type"
                },
                verified: {
                    dataType: "boolean",
                    name: "Verified",
                    readOnly: true
                },
                 seniority: {
                    dataType: "string",
                    name: "Seniority",
                    readOnly: true,
                    hideFromCollection: true,
                    disabled: {
                        hidden: true
                    }
                },
                years: {
                    dataType: "number",
                    name: "Years",
                    readOnly: true,
                    hideFromCollection: true,
                    disabled: {
                        hidden: true
                    }
                },
                score_val: {
                    dataType: "number",
                    name: "Score value",
                    readOnly: true,
                    hideFromCollection: true,
                    disabled: {
                        hidden: true
                    }
                },
                calculated_ref_score: {
                    dataType: "number",
                    name: "Calculated reference score",
                    readOnly: true,
                    hideFromCollection: true,
                    disabled: {
                        hidden: true
                    }
                },
                calculated_ref_count: {
                    dataType: "number",
                    name: "Calculated reference count",
                    readOnly: true
                },
                calculated_video_ref_count: {
                    dataType: "number",
                    name: "Calculated video reference count",
                    readOnly: true
                },
                calculated_text_ref_count: {
                    dataType: "number",
                    name: "Calculated text reference count",
                    readOnly: true,
                    hideFromCollection: true,
                    disabled: {
                        hidden: true
                    }
                },
                created_at: {
                    dataType: "date",
                    name: "Created At",
                    autoValue: "on_create",
                    readOnly: true
                },
                updated_at: {
                    dataType: "date",
                    name: "Updated At",
                    autoValue: "on_update",
                    readOnly: true
                },
                last_aggregated_at: {
                    dataType: "date",
                    name: "Last aggregated at",
                    readOnly: true,
                    hideFromCollection: true
                }
            },
             additionalFields: [
                {
                    key: "id",
                    name: "ID",
                    Builder: ({ entity }) => entity.id
                },
                {
                    key: "verified_by_names",
                    name: "Verified by",
                    Builder: ({ entity }) => {
                        const values = entity.values as any;
                        const verifiedBy = values.verified_by;
                        if (!Array.isArray(verifiedBy) || verifiedBy.length === 0) {
                            return <span>-</span>;
                        }
                        const names = verifiedBy.map((v: any) => v?.display_name).filter(Boolean).join(", ");
                        return <span>{names || "-"}</span>;
                    }
                }
            ],
            propertiesOrder: [
                "title",
                "role",
                "association",
                "summary",
                "industry",
                "function",
                "function_focus",
                "impact",
                "skills",
                "commitment_type",
                "verified",
                "calculated_ref_count",
                "calculated_video_ref_count",
                "verified_by_names",
                "updated_at",
                "created_at",
                "id",
                "last_aggregated_at"
            ]
        }),
        buildCollection({
            id: "dreference",
            name: "Digital Reference",
            path: "dreference",
            defaultSize: "xs",
            customId: false,
            properties: {
                accomplishment_ref: ({ path }: { path: string }) => ({
                    dataType: "reference" as const,
                    path: `resume/${path.split("/")[1] || "placeholder"}/accomplishment`,
                    name: "Accomplishment Reference"
                }),
                author_name: {
                    dataType: "string",
                    name: "Author Name"
                },
                author_mail: {
                    dataType: "string",
                    name: "Author Mail"
                },
                author_relationship: {
                    dataType: "string",
                    name: "Author Relationship"
                },
                author_company: {
                    dataType: "string",
                    name: "Author Company"
                },
                type: {
                    dataType: "string",
                    name: "Type"
                },
                status: {
                    dataType: "string",
                    name: "Status"
                },
                published: {
                    dataType: "boolean",
                    name: "Published"
                },
                message: {
                    dataType: "string",
                    name: "Message",
                    multiline: true
                },
                owner_name: {
                    dataType: "string",
                    name: "Owner Name"
                },
                author_uid: {
                    dataType: "string",
                    name: "Author UID",
                    hideFromCollection: true,
                    disabled: {
                        hidden: true
                    }
                },
                video_chunks: {
                    dataType: "array",
                    name: "Video Chunks",
                    of: {
                        dataType: "string"
                    }
                },
                created_at: {
                    dataType: "date",
                    name: "Created at",
                    autoValue: "on_create"
                },
                updated_at: {
                    dataType: "date",
                    name: "Updated at",
                    autoValue: "on_update"
                }
            },
            additionalFields: [
                {
                    key: "id",
                    name: "ID",
                    Builder: ({ entity }) => entity.id
                }
            ]
        })
    ],
    additionalFields: [
        {
            key: "id",
            name: "ID",
            Builder: ({ entity }) => entity.id
        },
        {
            key: "name_with_avatar",
            name: "Name",
            Builder: ({ entity }) => {
                const values = entity.values as any;
                const photoUrl = values.photo_url || "/images/default_avatar.svg";
                return (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <img 
                            src={photoUrl} 
                            alt={values.name} 
                            style={{ width: "24px", height: "24px", borderRadius: "50%", objectFit: "cover" }} 
                        />
                        <span>{values.name}</span>
                    </div>
                );
            }
        },
        {
            key: "is_verified_column",
            name: "Is Verified",
            Builder: ({ entity }) => {
                const values = entity.values as any;
                const isVerified = !!values.is_verified;
                return (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <input 
                            type="checkbox" 
                            checked={isVerified} 
                            readOnly 
                            style={{ pointerEvents: "none" }}
                        />
                        <span>{isVerified ? "Verified" : "Not Verified"}</span>
                    </div>
                );
            }
        }
    ],
    entityActions: [
        {
            key: "open_live",
            name: "Open",
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" height="20" viewBox="0 -960 960 960" width="20" fill="currentColor">
                    <path d="M200-120q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h280v80H200v560h560v-280h80v280q0-33-23.5-56.5T760-120H200Zm188-212-56-56 372-372H560v-80h280v280h-80v-144L388-332Z"/>
                </svg>
            ),
            collapsed: false,
            onClick: ({ entity }: any) => {
                const cleanUrl = entity.values.clean_url || "";
                const frontendDomain = import.meta.env.VITE_FRONTEND_URL || "http://localhost:4321";
                const domain = frontendDomain.endsWith("/") ? frontendDomain.slice(0, -1) : frontendDomain;
                const path = cleanUrl.startsWith("/") ? cleanUrl.slice(1) : cleanUrl;
                const url = `${domain}/resume/${path}`;
                window.open(url, "_blank");
            }
        }
    ],
    callbacks: {
        onPreSave: async ({ values }: any) => {
            const firstName = values.first_name || "";
            const lastName = values.last_nane || "";
            return {
                ...values,
                name: `${firstName} ${lastName}`.trim()
            };
        }
    },
    propertiesOrder: [
        "first_name",
        "last_nane",
        "photo_url",
        "name_with_avatar",
        "title",
        "bio",
        "references_count",
        "verified_accomplishments_count",
        "organization",
        "industry",
        "function",
        "skills_map",
        "is_verified_column",
        "is_open_to_work",
        "created_at",
        "updated_at",
        "last_aggregated_at",
        "subcollection:accomplishment",
        "subcollection:dreference",
        "location",
        "location_latlng",
        "user_function",
        "name",
        "email",
        "clean_url",
        "is_verified",
        "commitment_type",
        "commitment_type_breakdown",
        "function_breakdown",
        "function_focus",
        "industry_focus",
        "score",
        "score_tiers",
        "skills",
        "search_accomplishment_count",
        "search_companies",
        "search_keywords",
        "search_total_refs",
        "search_total_score",
        "search_total_years",
        "search_video_ref_count",
        "search_text_ref_count",
        "score_industry_saas",
        "score_industry_edtech",
        "score_function_engineering_development",
        "score_focus_platform_infrastructure_engineering",
        "score_focus_full_stack_development",
        "score_commitment_fractional_contributor",
        "score_commitment_type_fractional_contributor",
        "score_function_focus_full_stack_development",
        "score_function_focus_platform_infrastructure_engineering"
    ]
});
